/* eslint-disable complexity */
import React, { useContext, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import Box from 'carbon-react/lib/components/box';
import { Menu, MenuItem } from 'carbon-react/lib/components/menu';

import { Forecast } from '@futrli-sdk/predict';
import { FormattedMessage, useIntl } from '@futrli/localisation';
import { getConsolidatedForecastingUrl, PREDICT_PRODUCT_SLUG, ROUTES } from '@futrli/shared-services';
import { UIFeatherIcon } from '@futrli/ui-components';

import { ASSISTANT_TAB } from '../../../../ui/assistant-side-panel/assistant-side-panel';
import { ClosingForecastDialog } from './closing-forecast-dialog';
import { DeleteForecastDialog } from './delete-forecast-dialog';
import { ExcelExportDrawer } from '../../reports/external-sheets/export/excel-export-drawer';
import { ExportDrawer } from '../../reports/external-sheets/export/export-drawer';
import { ImportAssumptionsFromAdvisor } from './import-advisor-assumptions/import-assumptions-from-advisor';
import { SidePanelContext } from '../../context/side-panel';
import { SIZES } from '../../../../core/constants';
import { trackCTA } from '../../../../core/tracker';
import { usePredictFlags } from '../../../../hooks/usePredictFlags';
import { usePredictionsInfoContext } from '../../context/predictions-info-context';
import { UserGridSettingsContext } from '../../reports/user-grid-settings/user-grid-settings-context';

import styles from './predictions-nav-data-controls.scss';

export const ForecastingMenu = () => {
  const [isOpenExportModal, setIsOpenExportModal] = useState(false);
  const [isOpenExcelExportModal, setIsOpenExcelExportModal] = useState(false);
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false);
  const [isOpenAdvisorModal, setIsOpenAdvisorModal] = useState(false);

  const flags = useFlags();
  const { hasHideExportToGoogleSheetsEnabled } = usePredictFlags();
  const intl = useIntl();

  const {
    getShowZeroValueAccounts,
    setShowZeroValueAccounts,
    rowHeights,
    setRowHeights,
    showDecimals,
    setShowDecimals
  } = useContext(UserGridSettingsContext);
  const { forecast, deleteForecast, updateForecast, isConsolidation, isReadOnly } = usePredictionsInfoContext();
  const { openSidePanel } = useContext(SidePanelContext);
  const [isOpenCloseDialog, setIsOpenCloseDialog] = useState(false);
  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisationId } = useParams();
  const organisationId = tempOrganisationId || '';
  const workspaceId = tempWorkspaceId || '';

  const { pathname } = useLocation();

  const navigate = useNavigate();

  const handleShowUnusedAccountsChange = () => {
    if (forecast) {
      setShowZeroValueAccounts(forecast.id, !getShowZeroValueAccounts(forecast.id));
    }
  };

  const handleCloseCloseDialog = () => {
    setIsOpenCloseDialog(false);
  };

  const handleOpenCloseBudgetDialog = () => setIsOpenCloseDialog(true);

  const handleCloseBudget = () => {
    updateForecast?.({ is_frozen: true });
    handleCloseCloseDialog();
  };

  const handleOpenDeleteDialog = () => {
    setIsOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setIsOpenDeleteDialog(false);
  };

  const onDelete = () => {
    deleteForecast?.();
  };

  const handleRowHeightChange = () => {
    const heights = {
      default: rowHeights.default === SIZES.SMALL ? SIZES.MEDIUM : SIZES.SMALL
    };
    setRowHeights(heights);
  };

  const handleNewClick = () => {
    if (isConsolidation) {
      navigate(getConsolidatedForecastingUrl(workspaceId, organisationId, PREDICT_PRODUCT_SLUG));
    } else {
      navigate(
        `${ROUTES.WORKSPACES_ROUTE}/${workspaceId}${ROUTES.ORGANISATIONS_ROUTE}/${organisationId}${ROUTES.PREDICT}${ROUTES.FORECASTS}`
      );
    }
  };

  const handleExportClick = () => {
    trackCTA('Export', 'Export report');
    setIsOpenExportModal(true);
  };

  const handleExcelExportClick = () => setIsOpenExcelExportModal(true);

  const handleDecimalChange = () => {
    setShowDecimals(!showDecimals);
  };

  const handleSettingsClick = () => {
    openSidePanel?.(undefined, ASSISTANT_TAB.SETTINGS);
  };

  const handleImportFromAdvisorClick = () => {
    setIsOpenAdvisorModal(true);
  };

  const handleCloseImportFromAdvisorModal = () => {
    setIsOpenAdvisorModal(false);
  };

  const handleBulkUpdateClick = () => {
    openSidePanel?.(undefined, ASSISTANT_TAB.BULK_UPDATE_PREDICTIONS);
  };

  const onExportModalClose = () => {
    setIsOpenExportModal(false);
  };

  const onExcelExportModalClose = () => {
    setIsOpenExcelExportModal(false);
  };

  const isInvoicesPage = pathname.indexOf(ROUTES.INVOICES) !== -1;

  return (
    <Box>
      <Box className={styles.carbonMenu}>
        <Menu menuType="white">
          <MenuItem
            submenu={intl.formatMessage({ id: 'common.controls', defaultMessage: 'Controls' })}
            minWidth="170px"
          >
            {!isInvoicesPage && (
              <>
                <MenuItem onClick={handleShowUnusedAccountsChange}>
                  <Box display="flex" alignItems="center">
                    <FormattedMessage id="predict.show_unused_accounts" defaultMessage="Show unused accounts" />
                    {getShowZeroValueAccounts(forecast?.id || '') && (
                      <Box className={styles.iconContainer}>
                        <UIFeatherIcon iconName="check" />
                      </Box>
                    )}
                  </Box>
                </MenuItem>
                <MenuItem onClick={handleRowHeightChange}>
                  <Box display="flex" alignItems="center">
                    <FormattedMessage id="predict.compact_view" defaultMessage="Compact view" />
                    {rowHeights.default === SIZES.SMALL && (
                      <div className={styles.iconContainer}>
                        <UIFeatherIcon iconName="check" />
                      </div>
                    )}
                  </Box>
                </MenuItem>
                <MenuItem onClick={handleDecimalChange}>
                  <Box display="flex" alignItems="center">
                    <FormattedMessage id="predict.show_decimals" defaultMessage="Show decimals" />
                    {showDecimals && (
                      <div className={styles.iconContainer}>
                        <UIFeatherIcon iconName="check" />
                      </div>
                    )}
                  </Box>
                </MenuItem>
                {!isReadOnly && (
                  <MenuItem onClick={handleBulkUpdateClick}>
                    <FormattedMessage
                      id="predict.bulk_update_auto_predictions"
                      defaultMessage="Bulk update predictions"
                    />
                  </MenuItem>
                )}
                <li className={styles.dividerContainer} role="menuitem" aria-hidden="true">
                  <hr className={styles.divider} />
                </li>
              </>
            )}
            <MenuItem onClick={handleNewClick}>
              <FormattedMessage
                id="predict.new_budget_forecast_scenario"
                defaultMessage="New budget, forecast, scenario"
              />
            </MenuItem>
            {!isReadOnly &&
              !isConsolidation &&
              forecast?.kind === 'budget' &&
              !(forecast as Forecast).is_frozen &&
              forecast.status === 'published' && (
                <MenuItem onClick={handleOpenCloseBudgetDialog}>
                  <FormattedMessage id="predict.close_1_year_p_and_l" defaultMessage="Close this 1 year P&L" />
                </MenuItem>
              )}
            {!isReadOnly && (forecast?.kind === 'budget' || forecast?.kind === 'scenario') && (
              <MenuItem onClick={handleOpenDeleteDialog}>
                <FormattedMessage id="common.delete" defaultMessage="Delete" />
              </MenuItem>
            )}
            {!hasHideExportToGoogleSheetsEnabled && (
              <MenuItem onClick={handleExportClick}>
                <FormattedMessage id="predict.export_to_google_sheets" defaultMessage="Export to Google Sheets" />
              </MenuItem>
            )}
            {!isConsolidation && (
              <MenuItem onClick={handleExcelExportClick}>
                <FormattedMessage id="predict.export_to_excel" defaultMessage="Export to Excel" />
              </MenuItem>
            )}
            {!isReadOnly && !isConsolidation && (
              <MenuItem onClick={handleSettingsClick}>
                <FormattedMessage id="common.settings" defaultMessage="Settings" />
              </MenuItem>
            )}
            {!isReadOnly && flags.advisorMigration && !isConsolidation && !isInvoicesPage && (
              <MenuItem onClick={handleImportFromAdvisorClick}>
                <FormattedMessage
                  id="predict.import_assumptions_from_advisor"
                  defaultMessage="Import assumptions from Advisor"
                />
              </MenuItem>
            )}
          </MenuItem>
        </Menu>
      </Box>
      {!hasHideExportToGoogleSheetsEnabled && isOpenExportModal && (
        <ExportDrawer
          onClose={onExportModalClose}
          isConsolidation={!!isConsolidation}
          forecastId={forecast?.id}
          forecastKind={forecast?.kind}
          forecastName={forecast?.name}
        />
      )}

      {isOpenExcelExportModal && <ExcelExportDrawer onClose={onExcelExportModalClose} forecastId={forecast?.id} />}

      {isOpenCloseDialog && (
        <ClosingForecastDialog
          isOpenForecastingDialog={isOpenCloseDialog}
          handleCloseDialog={handleCloseCloseDialog}
          onSubmit={handleCloseBudget}
        />
      )}

      {isOpenDeleteDialog && (
        <DeleteForecastDialog
          isOpenForecastingDialog={isOpenDeleteDialog}
          onSubmit={onDelete}
          handleCloseDialog={handleCloseDeleteDialog}
          name={forecast?.name || ''}
        />
      )}

      {isOpenAdvisorModal && (
        <ImportAssumptionsFromAdvisor isOpen={isOpenAdvisorModal} handleClose={handleCloseImportFromAdvisorModal} />
      )}
    </Box>
  );
};
